import { FC, SyntheticEvent, useEffect, useRef } from 'react'
import { CheckPicker, CheckPickerProps } from 'rsuite'
import sortBy from 'lodash/sortBy'
import { ItemDataType } from 'rsuite/esm/@types/common'

import useForceUpdate from '../../../services/useForceUpdate'
import useFiltersClose from '../../hooks/useFiltersClose'
import useGlobalAnalyticsParams from '../../hooks/useGlobalAnalyticsParams'
import { hasProps } from '../../../utils/types/misc'

interface BaseFilterPickerProps extends CheckPickerProps<string> {
  shouldCloseFilters: boolean
  isSort?: boolean
  onChange?: (value: string[], event: SyntheticEvent) => void
  filterCode?: string
}

const BaseFilterPicker: FC<BaseFilterPickerProps> = (props) => {
  const {
    shouldCloseFilters,
    data,
    isSort = true,
    onChange,
    filterCode = '',
    value,
    disabledItemValues,
    ...rest
  } = props

  const forceUpdate = useForceUpdate()
  const isOpen = useFiltersClose(shouldCloseFilters)
  const [globalAnalyticsParams, setGlobalAnalyticsParams] = useGlobalAnalyticsParams()

  const { filterInteractionAmount = 0 } = globalAnalyticsParams

  const filtersRef = useRef<HTMLDivElement>(null)
  const containerRef = filtersRef.current || undefined

  const handleOnChangeTrigger = (newValues: string[], event: SyntheticEvent) => {
    if (onChange) {
      onChange(newValues, event)
    }

    // Label is a JSX element with the text as its first child
    const getSelectedFilterText = (
      dataValue: ItemDataType<string> | undefined,
    ): string | undefined => (
      hasProps(dataValue?.label)
        ? dataValue?.label?.props.children?.[0]
        : dataValue?.label
    )

    const selectedValues = data
      .filter((item) => newValues?.includes(item.value || ''))
      .map((item) => getSelectedFilterText(item)?.toLowerCase())
      .join('|')

    setGlobalAnalyticsParams({
      ...globalAnalyticsParams,
      selectedFilterValue: selectedValues || '',
      selectedFilterCode: filterCode,
      filterInteractionAmount: Number(filterInteractionAmount) + 1,
    })
  }

  useEffect(() => {
    forceUpdate() // to re-render for refs to implement
  }, [])

  const sortedData = isSort
    ? sortBy(data, (item) => (
      [disabledItemValues?.includes(item.value || ''), item.label]))
    : data
  return (
    <div
      ref={filtersRef}
      className="relative"
      data-testid={`${filterCode}-picker-container`}
    >
      <CheckPicker
        {...rest}
        value={value}
        onChange={handleOnChangeTrigger}
        data={sortedData}
        open={isOpen}
        container={containerRef}
        disabledItemValues={disabledItemValues}
        sticky
        block
        placement="bottomEnd"
        data-testid={`${filterCode}-picker`}
      />
    </div>
  )
}

export default BaseFilterPicker
